import { createEvent, createStore, sample } from 'effector'
import { fx } from 'src/libs'
import { setRequestError, setRequestSuccess } from 'src/features/notifications'
import type {
  Contest,
  ContestResponse,
  ContestStage,
  ListParams,
  Participant,
  ParticipantsResponse,
} from './types'

export const paramsChanged = createEvent<Partial<ListParams>>()
export const refresh = createEvent()
export const stageChanged = createEvent<ContestStage>()
export const disqualifyRequested = createEvent<{
  clientId: number
  reason: string
}>()
export const pointsChanged = createEvent<{
  clientId: number
  totalPoints: number
}>()

export const getParticipantsFx = fx<ListParams, ParticipantsResponse>({
  method: 'GET',
  url: '/promo/ib_contest_2026/participant',
})
export const getContestFx = fx<void, ContestResponse>({
  method: 'GET',
  url: '/promo/ib_contest_2026/promo',
})
export const changeStageFx = fx<any, void>({
  method: 'PUT',
  url: '/promo/ib_contest_2026/promo/{id}',
})
export const disqualifyFx = fx<any, void>({
  method: 'PUT',
  url: '/promo/ib_contest_2026/participant/{clientId}/disqualify',
})
export const pointsFx = fx<any, void>({
  method: 'POST',
  url: '/promo/ib_contest_2026/participant/{clientId}/points',
})

const initialParams: ListParams = {
  page: 1,
  limit: 100,
  orderBy: 'place',
  orderDirection: 'ascend',
  filters: {},
}
export const $params = createStore(initialParams).on(
  paramsChanged,
  (state, patch) => ({ ...state, ...patch }),
)
export const $participants = createStore<Participant[]>([]).on(
  getParticipantsFx.doneData,
  (_, response) => response.body.items,
)
export const $total = createStore(0).on(
  getParticipantsFx.doneData,
  (_, response) => response.body.total,
)
export const $contest = createStore<Contest | null>(null).on(
  getContestFx.doneData,
  (_, response) => response.body.items[0] ?? null,
)

sample({
  source: $params,
  clock: [
    $params,
    refresh,
    disqualifyFx.done,
    pointsFx.done,
    changeStageFx.done,
  ],
  fn: (params) => ({ query: buildQuery(params) }),
  target: getParticipantsFx,
})
sample({ clock: [refresh, $params], fn: () => ({}), target: getContestFx })
sample({
  source: $contest,
  clock: stageChanged,
  fn: (contest, status) => ({
    url: contest ? `/promo/ib_contest_2026/promo/${contest.id}` : '',
    body: { status },
  }),
  target: changeStageFx,
})
sample({
  clock: disqualifyRequested,
  fn: ({ clientId, reason }) => ({
    url: `/promo/ib_contest_2026/participant/${clientId}/disqualify`,
    body: { reason },
  }),
  target: disqualifyFx,
})
sample({
  clock: pointsChanged,
  fn: ({ clientId, totalPoints }) => ({
    url: `/promo/ib_contest_2026/participant/${clientId}/points`,
    body: { totalPoints },
  }),
  target: pointsFx,
})
sample({
  clock: [disqualifyFx.done, pointsFx.done],
  fn: () => true,
  target: setRequestSuccess,
})
sample({
  clock: [disqualifyFx.failData, pointsFx.failData],
  fn: (error) => ({
    code: error.status,
    message: formatApiError(error.body?.message),
  }),
  target: setRequestError,
})

function buildQuery(params: ListParams) {
  const query: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
    populate: JSON.stringify(['Client']),
  }
  if (params.orderBy)
    query.order = `${params.orderDirection === 'descend' ? '-' : ''}${
      params.orderBy
    }`
  const filters = Object.fromEntries(
    Object.entries(params.filters ?? {})
      .map(([key, value]) => {
        if (key === 'clientId') return ['client.id', value]
        if (key === 'clientName') return ['client.tsname', value]
        if (key === 'country') return ['client.country', value]
        return [key, value]
      })
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0),
      ),
  )
  if (Object.keys(filters).length) query.where = JSON.stringify(filters)
  return query as Record<string, string>
}

function formatApiError(error: unknown): string {
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error)
    } catch {
      return 'Failed to update participant points'
    }
  }
  return 'Failed to update participant points'
}
